import { createClient, User } from '@supabase/supabase-js';
import { Database } from './db';
import { Missionary, Subscriber, UnsubscribeData } from './types';

// const supabase = createClient(`${process.env.REACT_APP_SUPABASE_URL}`,`${process.env.REACT_APP_SUPABASE_ANON_KEY}`);

// TODO: Use environment variables with this
export const supabase = createClient(
    process.env.REACT_APP_SUPABASE_URL!,
    process.env.REACT_APP_SUPABASE_ANON_KEY!
);

export async function getSession() {
    const res = await supabase.auth.getSession();
    console.log(res);
    return res.data.session;
}

export async function signInWithPassword(email: string, password: string) {
    return await supabase.auth.signInWithPassword({ email: email, password: password });
}

export async function signUpWithPassword(email: string, password: string) {
    return await supabase.auth.signUp({
        email: email,
        password: password,
        options: {
            emailRedirectTo: 'http://app.pressforwardmail.com/dashboard' // TODO: Set this as an environment variable
        }
    });
}

export async function requestPasswordReset(email: string, redirectTo?: string) {
    return await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: redirectTo || `${window.location.origin}/reset-password`
    });
}

export async function resetPassword(password: string) {
    return await supabase.auth.updateUser({ password: password });
}

export async function signout() {
    return await supabase.auth.signOut();
}

export async function getMissionariesByUser(user: User): Promise<Missionary[]> {
    const res = await supabase
        .from('missionary')
        .select(
            'missionary_id, name, email, unique_id, assigned_mission, start_date, subscription_status'
        )
        .filter('user_id', 'eq', user.id);
    return res.data as Missionary[];
}

export async function getMissionaryByUniqueId(id: string): Promise<Missionary | null> {
    const res = await supabase
        .from('missionary')
        .select('missionary_id, name, email, unique_id, assigned_mission, start_date')
        .filter('unique_id', 'eq', id);
    console.log(res);
    if (res.data) {
        return res.data[0] as Missionary;
    }
    return null;
}

export async function insertMissionary(missionary: Missionary) {
    return await supabase.from('missionary').insert(missionary);
}

export async function editMissionary(missionary: Missionary) {
    await supabase.from('missionary').update(missionary).eq('unique_id', missionary.unique_id);
}

export const deleteMissionary = async (missionary: Missionary) => {
    if (missionary.unique_id) {
        const { error } = await supabase
            .from('missionary')
            .delete()
            .eq('unique_id', missionary.unique_id);

        if (error) {
            throw error;
        }
    } else {
        throw Error('Missionary does not have a unique_id.');
    }
};

export async function insertSubscriber(subscriber: Subscriber) {
    await supabase.from('subscriber').insert(subscriber);
}

export async function getSubscribersByMissionary(id: string) {
    const res = await supabase
        .from('subscriber')
        .select('subscriber_id, email, missionary, active, verified')
        .filter('missionary', 'eq', id);
    return res.data as Subscriber[];
}

export async function deleteSubscriber(subscriber: Subscriber) {
    const { data, error } = await supabase.from('subscriber').delete().eq('email', subscriber.email).eq('missionary', subscriber.missionary);
    if (error) {
        throw "Error deleting subscriber.";
    }
    return data;
}

export async function getMissionaryByUniqueIdForUnsubscribe(uniqueId: string) {
    const { data, error } = await supabase.from('missionary').select('name').eq('unique_id', uniqueId);
    if (error) {
        throw "Error retrieving missionary data.";
    }
    return data?.[0] || null;
}

export async function getUnsubscribeToken(token: string): Promise<UnsubscribeData | null> {
    const { data, error } = await supabase.from('unsubscribe_tokens').select('missionary_id, subscriber_email, expires_at, used').eq('token', token);
    if (error) {
        throw "Error retrieving token data.";
    }
    return data?.[0] || null;
}

export async function getMissionaryName(id: string): Promise<string | null> {
    const res = await supabase
        .from('missionary')
        .select('name')
        .eq('unique_id', id);
    if (res.data && res.data.length > 0) {
        return res.data[0]?.name as string;
    }
    return null;
}

export async function editSubscriber(subscriber: Subscriber) {
    const res = await supabase.from('subscriber').upsert(subscriber);
    return res;
}

export async function getPage(name: string) {
    const res = await supabase.from('pages').select('name, content').filter('name', 'eq', name);
    if (res !== null && res.data !== null && res.data.length > 0) {
        return res.data[0];
    } else {
        return null;
    }
}

export async function getPlans() {
    const res = await supabase.from('plans').select('id, name, price, duration, description');
    return res?.data;
}

export async function getGlobalData(
    key?: string
): Promise<{ key: string; value: string; data_type: string }[] | null> {
    const query = supabase.from('globaldata').select('key, value, data_type');
    if (key) {
        return (await query.eq('key', key)).data;
    }
    return (await query).data;
}
